/**
 * SiteSettingsDrawer — T3.19 + T3.20 + T3.21.
 *
 * A right-edge sidebar with three tabs:
 *   • Slug     — vanity slug claim form (PS6)
 *   • Media    — list of uploaded assets (PS7)
 *   • History  — list of published versions (PS8 stub until M4)
 *
 * Kept as one component instead of three separate drawers because all
 * three are settings-y, low-frequency interactions that share the same
 * "lives next to the editor canvas, doesn't take focus from it" shape.
 * Easier for the user to mental-model one drawer with tabs than three
 * gear icons.
 *
 * Rendering choices:
 *   • Chrome is @duro-app/ui: a right-anchored `Drawer` (backdrop +
 *     click-outside/escape dismiss via onOpenChange → onClose) wrapping
 *     `Tabs`; SlugTab uses DS `Input`/`Button`, NavTab a DS `Switch`.
 *   • Tab switching is local state; `Tabs.Panel` lazily mounts the active
 *     tab, so loaders still fire only on activation.
 *   • The slug form is the only mutating surface; success refreshes
 *     `site.slug` via the onSlugChanged callback.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Schema } from "effect";
import { css, html } from "react-strict-dom";
import {
  Badge,
  Button,
  Drawer,
  Field,
  Input,
  Spinner,
  Switch,
  Tabs,
  Tooltip,
} from "@duro-app/ui";
import { Form } from "@duro-app/ui/form";
import { colors } from "@duro-app/tokens/tokens/colors.css";
import { borders } from "@duro-app/tokens/tokens/borders.css";
import { sizes } from "@duro-app/tokens/tokens/sizes.css";
import {
  microSpacing,
  radii,
  spacing,
} from "@duro-app/tokens/tokens/spacing.css";
import { typography } from "@duro-app/tokens/tokens/typography.css";
import { duration, easing } from "@duro-app/tokens/tokens/motion.css";

import { api, ApiError, type AssetSummary } from "../api/client.js";
import {
  gitLog,
  gitRestore,
  type VersionCommit,
} from "../publish/gitClient.js";
import { publishErrorMessage } from "../publish/publishError.js";
import { useIsMobile } from "@website-builder/editor";
import type { SiteTheme } from "@website-builder/schemas";
import { relativeTime, absoluteTime } from "../util/relativeTime.js";
import { BrandTab } from "./BrandTab.js";

type Tab = "slug" | "media" | "history" | "nav" | "brand";

interface Props {
  readonly siteId: string;
  readonly currentSlug: string;
  readonly publicHost: string;
  readonly canEdit: boolean;
  /** Cloud-only surfaces (slug/media/history) need a published site. Off for
   *  local drafts, which still get Brand + Nav. Defaults on (cloud sites). */
  readonly cloudFeatures?: boolean;
  readonly onClose: () => void;
  readonly onSlugChanged: (newSlug: string) => void;
  /** Preview a past version read-only on the editor canvas. */
  readonly onPreview: (oid: string, label: string, committedAt: number) => void;
  /** Current "nested published nav" setting; when paired with
   *  onNavNestedChange (and canEdit) a Nav tab is shown. */
  readonly navNested?: boolean;
  readonly onNavNestedChange?: (nested: boolean) => void;
  /** Current Brand theme; with onThemeChange (and canEdit) a Brand tab is shown. */
  readonly theme?: SiteTheme;
  readonly onThemeChange?: (theme: SiteTheme) => void;
}

export function SiteSettingsDrawer({
  siteId,
  currentSlug,
  publicHost,
  canEdit,
  cloudFeatures = true,
  onClose,
  onSlugChanged,
  onPreview,
  navNested,
  onNavNestedChange,
  theme,
  onThemeChange,
}: Props) {
  const showNav = canEdit && onNavNestedChange !== undefined;
  const showBrand =
    canEdit && onThemeChange !== undefined && theme !== undefined;
  const tabs: Tab[] = [
    ...(cloudFeatures ? (["slug", "media", "history"] as Tab[]) : []),
    ...(showBrand ? (["brand"] as Tab[]) : []),
    ...(showNav ? (["nav"] as Tab[]) : []),
  ];
  const [tab, setTab] = useState<Tab>(tabs[0] ?? "brand");
  const tabLabel = (id: Tab): string =>
    id === "history"
      ? "History"
      : id === "media"
        ? "Media"
        : id === "nav"
          ? "Nav"
          : id === "brand"
            ? "Brand"
            : "URL";

  return (
    <Drawer.Root
      open
      anchor="right"
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <Drawer.Portal size="md">
        <Drawer.Header>
          <Drawer.Title>
            {cloudFeatures ? "Site settings" : "Brand & theme"}
          </Drawer.Title>
          <Drawer.Close />
        </Drawer.Header>
        <Drawer.Body>
          <Tabs.Root value={tab} onValueChange={(v) => setTab(v as Tab)}>
            <Tabs.List>
              {tabs.map((id) => (
                <Tabs.Tab key={id} value={id}>
                  {tabLabel(id)}
                </Tabs.Tab>
              ))}
            </Tabs.List>
            {cloudFeatures && (
              <>
                <Tabs.Panel value="slug">
                  <SlugTab
                    siteId={siteId}
                    currentSlug={currentSlug}
                    publicHost={publicHost}
                    canEdit={canEdit}
                    onSlugChanged={onSlugChanged}
                  />
                </Tabs.Panel>
                <Tabs.Panel value="media">
                  <MediaTab siteId={siteId} />
                </Tabs.Panel>
                <Tabs.Panel value="history">
                  <HistoryTab
                    siteId={siteId}
                    canEdit={canEdit}
                    onPreview={onPreview}
                  />
                </Tabs.Panel>
              </>
            )}
            {showBrand && theme && onThemeChange && (
              <Tabs.Panel value="brand">
                <BrandTab
                  theme={theme}
                  onChange={onThemeChange}
                  canEdit={canEdit}
                />
              </Tabs.Panel>
            )}
            {showNav && onNavNestedChange && (
              <Tabs.Panel value="nav">
                <NavTab
                  nested={navNested ?? false}
                  onChange={onNavNestedChange}
                />
              </Tabs.Panel>
            )}
          </Tabs.Root>
        </Drawer.Body>
      </Drawer.Portal>
    </Drawer.Root>
  );
}

// ---------- Styles

// The live dot's soft pulsing ring and the freshly-restored row's green
// flash. Both are web-only `css.keyframes` (this drawer is web-only).
const pulse = css.keyframes({
  "0%": { transform: "scale(1)", opacity: 0.5 },
  "50%": { transform: "scale(1.6)", opacity: 0 },
  "100%": { transform: "scale(1)", opacity: 0.5 },
});

const flashFade = css.keyframes({
  from: { backgroundColor: colors.successBg },
  to: { backgroundColor: "transparent" },
});

const REDUCED_MOTION = "@media (prefers-reduced-motion: reduce)";
const HOVER = "@media (hover: hover)";

const styles = css.create({
  navTab: {
    display: "flex",
    alignItems: "flex-start",
    gap: spacing.sm,
  },
  navTitle: {
    fontWeight: typography.fontWeightSemibold,
    color: colors.text,
  },
  navHint: {
    display: "block",
    fontSize: typography.fontSizeXs,
    color: colors.textMuted,
    marginTop: spacing.xs,
  },
  intro: {
    marginTop: 0,
    color: colors.textMuted,
    fontSize: typography.fontSizeSm,
  },
  code: {
    backgroundColor: colors.bgCard,
    paddingBlock: microSpacing.px2,
    paddingInline: microSpacing.px6,
  },
  slugRow: {
    display: "flex",
    alignItems: "center",
    gap: spacing.xs,
  },
  grow: {
    flex: 1,
    minWidth: 0,
  },
  hostSuffix: {
    color: colors.textMuted,
    fontSize: typography.fontSizeSm,
  },
  slugRules: {
    fontSize: typography.fontSizeXs,
    color: colors.textMuted,
    marginTop: spacing.sm,
    marginBottom: spacing.ms,
    marginInline: 0,
  },
  message: {
    marginTop: spacing.ms,
    fontSize: typography.fontSizeSm,
  },
  error: {
    color: colors.error,
  },
  success: {
    color: colors.success,
  },
  muted: {
    color: colors.textMuted,
  },
  mutedSm: {
    color: colors.textMuted,
    fontSize: typography.fontSizeSm,
  },
  list: {
    listStyle: "none",
    padding: 0,
    margin: 0,
  },
  assetRow: {
    display: "flex",
    alignItems: "center",
    gap: spacing.ms,
    paddingBlock: spacing.sm,
    paddingInline: 0,
    borderBottomWidth: borders.hairline,
    borderBottomStyle: "solid",
    borderBottomColor: colors.border,
    fontSize: typography.fontSizeSm,
  },
  assetThumb: {
    width: sizes.thumbSm,
    height: sizes.thumbSm,
    borderRadius: radii.xs,
    backgroundColor: colors.bgCard,
    fontSize: typography.fontSizeXs,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: colors.textMuted,
    flexShrink: 0,
  },
  ellipsis: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  assetMeta: {
    color: colors.textMuted,
    fontSize: typography.fontSizeXs,
  },
  skeleton: (opacity: number) => ({
    height: sizes.controlLg,
    marginBlock: spacing.sm,
    marginInline: 0,
    borderRadius: radii.px6,
    backgroundColor: colors.bgCard,
    opacity,
  }),
  loadingNote: {
    color: colors.textMuted,
    fontSize: typography.fontSizeXs,
  },
  errorBox: {
    fontSize: typography.fontSizeSm,
  },
  retry: {
    marginTop: spacing.sm,
  },
  actionError: {
    color: colors.error,
    fontSize: typography.fontSizeXs,
    marginTop: 0,
    marginInline: 0,
    marginBottom: spacing.sm,
  },
  row: {
    display: "flex",
    gap: spacing.sm,
    borderRadius: radii.px6,
    backgroundColor: {
      default: null,
      [HOVER]: { default: null, ":hover": colors.bgCardHover },
    },
    transitionProperty: "background-color",
    transitionDuration: { default: duration.quick, [REDUCED_MOTION]: "0s" },
    transitionTimingFunction: easing.standard,
  },
  rowFlash: {
    animationName: { default: flashFade, [REDUCED_MOTION]: "none" },
    // holds-until: Duro motion has long-animation lengths (https://github.com/fredericrous/duro-design-system/issues/109); the 280ms scale would change this 1s fade.
    animationDuration: "1s",
    animationTimingFunction: easing.easeOut,
  },
  rail: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: sizes.glyphMd,
    paddingTop: spacing.ms,
  },
  dotWrap: {
    position: "relative",
    flexShrink: 0,
    display: "flex",
  },
  dot: {
    width: sizes.indicatorDot,
    height: sizes.indicatorDot,
    borderRadius: radii.full,
    backgroundColor: colors.bgCard,
    borderWidth: borders.strong,
    borderStyle: "solid",
    borderColor: colors.border,
  },
  dotLive: {
    backgroundColor: colors.success,
    borderColor: colors.success,
  },
  // The live dot's ring — a real element in place of the old ::after.
  pulseRing: {
    position: "absolute",
    top: `calc(-1 * ${spacing.xs})`,
    right: `calc(-1 * ${spacing.xs})`,
    bottom: `calc(-1 * ${spacing.xs})`,
    left: `calc(-1 * ${spacing.xs})`,
    borderRadius: radii.full,
    borderWidth: borders.strong,
    borderStyle: "solid",
    borderColor: colors.success,
    opacity: { default: 0.5, [REDUCED_MOTION]: 0.4 },
    animationName: { default: pulse, [REDUCED_MOTION]: "none" },
    // holds-until: Duro motion has long-animation lengths (https://github.com/fredericrous/duro-design-system/issues/109); the 280ms scale would change this 2s pulse.
    animationDuration: "2s",
    animationTimingFunction: easing.standard,
    animationIterationCount: "infinite",
    pointerEvents: "none",
  },
  railLine: {
    flex: 1,
    width: borders.strong,
    backgroundColor: colors.border,
  },
  rowToggle: (isMobile: boolean) => ({
    display: "flex",
    alignItems: "center",
    gap: spacing.sm,
    width: "100%",
    minHeight: isMobile ? 56 : sizes.touchTarget,
    paddingBlock: spacing.sm,
    paddingInline: 0,
    backgroundColor: "transparent",
    borderWidth: 0,
    cursor: "pointer",
    textAlign: "left",
  }),
  versionHead: {
    display: "flex",
    alignItems: "center",
    gap: spacing.sm,
  },
  versionNo: {
    fontWeight: typography.fontWeightSemibold,
    color: colors.text,
    fontSize: typography.fontSizeSm,
  },
  time: {
    display: "block",
    color: colors.textMuted,
    fontSize: typography.fontSizeXs,
    marginTop: microSpacing.px2,
  },
  // Desktop hover shortcut: hidden until the row is hovered or open, on
  // hover-capable pointers only (touch uses the tap-expand instead).
  restore: {
    fontSize: typography.fontSizeXs,
    color: colors.accent,
    whiteSpace: "nowrap",
    transitionProperty: "opacity, transform",
    transitionDuration: { default: duration.fast, [REDUCED_MOTION]: "0s" },
    transitionTimingFunction: easing.standard,
  },
  restoreHidden: {
    opacity: { default: null, [HOVER]: { default: 0, [REDUCED_MOTION]: 1 } },
    transform: {
      default: null,
      [HOVER]: {
        default: `translateX(${spacing.xs})`,
        [REDUCED_MOTION]: "none",
      },
    },
    pointerEvents: {
      default: null,
      [HOVER]: { default: "none", [REDUCED_MOTION]: "auto" },
    },
  },
  chevron: {
    color: colors.textMuted,
    fontSize: typography.fontSizeXs,
    transitionProperty: "transform",
    transitionDuration: { default: duration.fast, [REDUCED_MOTION]: "0s" },
    transitionTimingFunction: easing.standard,
  },
  chevronOpen: {
    transform: "rotate(90deg)",
  },
  // Inline confirm accordion: a 0fr → 1fr grid row animates to the
  // content's own height, no max-height cap needed.
  confirm: {
    display: "grid",
    gridTemplateRows: "0fr",
    opacity: 0,
    transitionProperty: "grid-template-rows, opacity",
    transitionDuration: { default: duration.base, [REDUCED_MOTION]: "0s" },
    transitionTimingFunction: easing.standard,
  },
  confirmOpen: {
    gridTemplateRows: "1fr",
    opacity: 1,
  },
  confirmClip: {
    overflow: "hidden",
    minHeight: 0,
  },
  confirmBody: {
    paddingTop: 0,
    paddingInline: 0,
    paddingBottom: spacing.ms,
    fontSize: typography.fontSizeXs,
    color: colors.textMuted,
  },
  confirmText: {
    marginTop: 0,
    marginInline: 0,
    marginBottom: spacing.sm,
  },
  confirmActions: {
    display: "flex",
    gap: spacing.sm,
  },
});

// ---------- Nav tab (nested-menu toggle) ----------

function NavTab({
  nested,
  onChange,
}: {
  readonly nested: boolean;
  readonly onChange: (nested: boolean) => void;
}) {
  return (
    <html.div style={styles.navTab}>
      <Switch
        checked={nested}
        onCheckedChange={onChange}
        aria-label="Nested navigation menu"
      />
      <html.span>
        <html.span style={styles.navTitle}>Nested navigation menu</html.span>
        <html.span style={styles.navHint}>
          Build a hierarchy by nesting pages under one another; the published
          site's menu then shows parents with their child pages as a dropdown.
          When off, the menu is a single flat list (the original behaviour).
        </html.span>
      </html.span>
    </html.div>
  );
}

// ---------- Slug tab (T3.19)

const SlugSchema = Schema.Struct({ slug: Schema.String });
type SlugValues = typeof SlugSchema.Type;

function SlugTab({
  siteId,
  currentSlug,
  publicHost,
  canEdit,
  onSlugChanged,
}: {
  readonly siteId: string;
  readonly currentSlug: string;
  readonly publicHost: string;
  readonly canEdit: boolean;
  readonly onSlugChanged: (s: string) => void;
}) {
  const [draft, setDraft] = useState(currentSlug);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  async function handleSubmit(): Promise<void> {
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      const r = await api.claimSlug(siteId, { slug: draft.trim() });
      setSuccess(
        `Your site is live at ${r.slug}.${publicHost}. The old URL ${r.previousSlug}.${publicHost} redirects for 90 days.`,
      );
      onSlugChanged(r.slug);
    } catch (e) {
      if (e instanceof ApiError) {
        const body = e.body as { error?: string; reason?: string };
        if (body?.error === "slug_taken")
          setError("That URL is already taken.");
        else if (body?.error === "slug_reserved")
          setError("That URL is reserved.");
        else if (body?.error === "slug_invalid")
          setError(`Invalid URL: ${body.reason ?? "see naming rules"}.`);
        else setError(`Error ${e.status}.`);
      } else {
        setError(String(e));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <html.div>
      <html.p style={styles.intro}>
        Your site is currently at{" "}
        <html.code style={styles.code}>
          {currentSlug}.{publicHost}
        </html.code>
        . Claim a custom URL — the old URL keeps redirecting for 90 days.
      </html.p>
      <Form<SlugValues>
        schema={SlugSchema}
        defaultValues={{ slug: currentSlug }}
        onSubmit={handleSubmit}
      >
        {({ setValue }) => (
          <html.div>
            <Field.Root name="slug" disabled={!canEdit || submitting}>
              <Field.Label>Custom URL</Field.Label>
              <html.div style={styles.slugRow}>
                <html.div style={styles.grow}>
                  <Input
                    type="text"
                    value={draft}
                    onChange={(e) => {
                      const next = e.target.value.toLowerCase();
                      setDraft(next);
                      setValue("slug", next);
                    }}
                    disabled={!canEdit || submitting}
                    placeholder="mysite"
                  />
                </html.div>
                <html.span style={styles.hostSuffix}>.{publicHost}</html.span>
              </html.div>
            </Field.Root>
            <html.p style={styles.slugRules}>
              3-32 chars, lowercase letters / digits / hyphens. No
              leading/trailing hyphen.
            </html.p>
            <Button
              type="submit"
              variant="primary"
              disabled={!canEdit || submitting || draft === currentSlug}
            >
              {submitting ? "Claiming…" : "Claim URL"}
            </Button>
            {error && (
              <html.p style={[styles.message, styles.error]}>{error}</html.p>
            )}
            {success && (
              <html.p style={[styles.message, styles.success]}>
                {success}
              </html.p>
            )}
          </html.div>
        )}
      </Form>
    </html.div>
  );
}

// ---------- Media tab (T3.20)

/** ApiError carries a status; anything else is unexpected. Keeps raw
 *  "ApiError: api error 401" strings out of the UI. */
function assetErrorMessage(e: unknown): string {
  if (e instanceof ApiError)
    return e.status === 401 || e.status === 403
      ? "Your session expired — sign in again to see media."
      : `Couldn't load media (error ${e.status}).`;
  return "Couldn't load media.";
}

function MediaTab({ siteId }: { readonly siteId: string }) {
  const [state, setState] = useState<
    | { kind: "loading" }
    | { kind: "ready"; assets: ReadonlyArray<AssetSummary> }
    | { kind: "error"; message: string }
  >({ kind: "loading" });
  // `active` mirrors HistoryTab's loader: without it a fast site switch lets
  // the previous site's assets land in the new site's list. `String(e)` also
  // rendered "ApiError: api error 401" at the user, so map the status.
  useEffect(() => {
    let active = true;
    api.listAssets(siteId).then(
      (assets) => {
        if (active) setState({ kind: "ready", assets });
      },
      (e: unknown) => {
        if (active) setState({ kind: "error", message: assetErrorMessage(e) });
      },
    );
    return () => {
      active = false;
    };
  }, [siteId]);

  if (state.kind === "loading") return <html.p>Loading…</html.p>;
  if (state.kind === "error")
    return <html.p style={styles.error}>{state.message}</html.p>;
  if (state.assets.length === 0)
    return (
      <html.p style={styles.muted}>
        No media uploaded yet. Drop an image into an image widget to upload.
      </html.p>
    );

  return (
    <html.ul style={styles.list}>
      {state.assets.map((a) => (
        <html.li key={a.hash} style={styles.assetRow}>
          <html.div style={styles.assetThumb}>
            {a.contentType.startsWith("image/") ? "IMG" : "FILE"}
          </html.div>
          <html.div style={styles.grow}>
            <html.div style={styles.ellipsis}>{a.filename}</html.div>
            <html.div style={styles.assetMeta}>
              {formatBytes(a.sizeBytes)} ·{" "}
              {new Date(a.uploadedAt).toLocaleDateString()}
            </html.div>
          </html.div>
        </html.li>
      ))}
    </html.ul>
  );
}

function formatBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

// ---------- History tab (T3.21)

type HistoryState =
  | { kind: "loading" }
  | { kind: "ready"; commits: ReadonlyArray<VersionCommit>; headOid: string }
  | { kind: "error"; message: string };

function HistoryTab({
  siteId,
  canEdit,
  onPreview,
}: {
  readonly siteId: string;
  readonly canEdit: boolean;
  readonly onPreview: (oid: string, label: string, committedAt: number) => void;
}) {
  const isMobile = useIsMobile();
  const [state, setState] = useState<HistoryState>({ kind: "loading" });
  const [openOid, setOpenOid] = useState<string | null>(null);
  const [restoringOid, setRestoringOid] = useState<string | null>(null);
  const [flashOid, setFlashOid] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  // Cleared on unmount so the flash timer can't fire into a dead component.
  const flashTimer = useRef<number | undefined>(undefined);
  useEffect(
    () => () => {
      if (flashTimer.current !== undefined)
        window.clearTimeout(flashTimer.current);
    },
    [],
  );

  const reload = useCallback(async (): Promise<void> => {
    setState({ kind: "loading" });
    setActionError(null);
    try {
      const r = await gitLog(siteId);
      setState(
        r.ok
          ? { kind: "ready", commits: r.commits, headOid: r.headOid }
          : { kind: "error", message: publishErrorMessage(r.failure) },
      );
    } catch (e) {
      setState({
        kind: "error",
        message: e instanceof Error ? e.message : String(e),
      });
    }
  }, [siteId]);

  useEffect(() => {
    if (!canEdit) return;
    let active = true;
    setState({ kind: "loading" });
    setActionError(null);
    gitLog(siteId).then(
      (r) => {
        if (!active) return;
        setState(
          r.ok
            ? { kind: "ready", commits: r.commits, headOid: r.headOid }
            : { kind: "error", message: publishErrorMessage(r.failure) },
        );
      },
      (e: unknown) => {
        if (active)
          setState({
            kind: "error",
            message: e instanceof Error ? e.message : String(e),
          });
      },
    );
    return () => {
      active = false;
    };
  }, [siteId, canEdit]);

  async function handleRestore(oid: string): Promise<void> {
    // Guard re-entry: a double-click would otherwise fire two restores, i.e.
    // two rollback commits.
    if (restoringOid !== null) return;
    setRestoringOid(oid);
    setActionError(null);
    try {
      const r = await gitRestore(siteId, oid);
      if (!r.ok) {
        setActionError(
          r.rejected
            ? "Someone published in the meantime — refresh and try again."
            : (r.error ?? "Restore failed."),
        );
        return;
      }
      setOpenOid(null);
      const res = await gitLog(siteId);
      if (res.ok) {
        setState({ kind: "ready", commits: res.commits, headOid: res.headOid });
        setFlashOid(res.headOid);
        flashTimer.current = window.setTimeout(
          () => setFlashOid((cur) => (cur === res.headOid ? null : cur)),
          1100,
        );
      } else {
        // The restore DID land; only the refresh failed. Say so rather than
        // leaving a stale timeline that looks like nothing happened.
        setActionError(
          `Restored, but the version list couldn't be refreshed. ${publishErrorMessage(
            res.failure,
          )}`,
        );
      }
    } catch (e) {
      // Worker boot/crash/timeout rejects. Without this the `finally` below
      // never ran, so every row stayed disabled behind a stuck "Restoring…".
      setActionError(e instanceof Error ? e.message : "Restore failed.");
    } finally {
      setRestoringOid(null);
    }
  }

  if (!canEdit)
    return (
      <html.p style={styles.mutedSm}>
        Version history is available to the site owner.
      </html.p>
    );

  if (state.kind === "loading")
    return (
      <html.div aria-busy={true}>
        {[0, 1, 2].map((i) => (
          <html.div key={i} style={styles.skeleton(1 - i * 0.25)} />
        ))}
        <html.p style={styles.loadingNote}>Loading history…</html.p>
      </html.div>
    );

  if (state.kind === "error")
    return (
      <html.div style={styles.errorBox}>
        <html.p style={styles.error}>{state.message}</html.p>
        <html.div style={styles.retry}>
          <Button
            variant="secondary"
            size="small"
            onClick={() => void reload()}
          >
            Retry
          </Button>
        </html.div>
      </html.div>
    );

  if (state.commits.length === 0)
    return (
      <html.p style={styles.mutedSm}>
        Publish your site to start a version history.
      </html.p>
    );

  const total = state.commits.length;
  const versionOf = new Map(state.commits.map((c, i) => [c.oid, total - i]));

  return (
    <html.div>
      {actionError && (
        <html.p role="alert" style={styles.actionError}>
          {actionError}
        </html.p>
      )}
      <html.ul style={styles.list}>
        {state.commits.map((c, i) => {
          const isLive = c.oid === state.headOid;
          const restoredFromV = c.restoredFrom
            ? versionOf.get(c.restoredFrom)
            : undefined;
          return (
            <VersionRow
              key={c.oid}
              commit={c}
              versionNo={total - i}
              isLive={isLive}
              isLast={i === total - 1}
              isOpen={openOid === c.oid}
              isRestoring={restoringOid === c.oid}
              isFlashing={flashOid === c.oid}
              restoredFromV={restoredFromV}
              isMobile={isMobile}
              busy={restoringOid !== null}
              onToggle={() =>
                setOpenOid((cur) => (cur === c.oid ? null : c.oid))
              }
              onRestore={() => void handleRestore(c.oid)}
              onPreview={() => onPreview(c.oid, `v${total - i}`, c.committedAt)}
            />
          );
        })}
      </html.ul>
    </html.div>
  );
}

function VersionRow({
  commit,
  versionNo,
  isLive,
  isLast,
  isOpen,
  isRestoring,
  isFlashing,
  restoredFromV,
  isMobile,
  busy,
  onToggle,
  onRestore,
  onPreview,
}: {
  readonly commit: VersionCommit;
  readonly versionNo: number;
  readonly isLive: boolean;
  readonly isLast: boolean;
  readonly isOpen: boolean;
  readonly isRestoring: boolean;
  readonly isFlashing: boolean;
  readonly restoredFromV: number | undefined;
  readonly isMobile: boolean;
  readonly busy: boolean;
  readonly onToggle: () => void;
  readonly onRestore: () => void;
  readonly onPreview: () => void;
}) {
  // Per-row hover (was an ancestor-hover CSS selector): reveals the
  // desktop Restore shortcut while the pointer is over the row.
  const [hovered, setHovered] = useState(false);
  return (
    <html.li
      style={[styles.row, isFlashing && styles.rowFlash]}
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
    >
      {/* timeline rail */}
      <html.div style={styles.rail} aria-hidden={true}>
        <html.span style={styles.dotWrap}>
          <html.span style={[styles.dot, isLive && styles.dotLive]} />
          {isLive && <html.span style={styles.pulseRing} />}
        </html.span>
        {!isLast && <html.span style={styles.railLine} />}
      </html.div>

      {/* body */}
      <html.div style={styles.grow}>
        <html.button
          type="button"
          onClick={onToggle}
          aria-expanded={isOpen}
          style={styles.rowToggle(isMobile)}
        >
          <html.span style={styles.grow}>
            <html.span style={styles.versionHead}>
              <html.span style={styles.versionNo}>v{versionNo}</html.span>
              {isLive && (
                <Badge variant="success" size="sm">
                  Live
                </Badge>
              )}
              {restoredFromV !== undefined && (
                <Badge size="sm">restored from v{restoredFromV}</Badge>
              )}
            </html.span>
            <html.span style={styles.time}>
              <Tooltip.Root content={absoluteTime(commit.committedAt)}>
                <Tooltip.Trigger>
                  {relativeTime(commit.committedAt)}
                </Tooltip.Trigger>
              </Tooltip.Root>
            </html.span>
          </html.span>

          {/* desktop hover shortcut; hidden when live */}
          {!isMobile && !isLive && (
            <html.span
              style={[
                styles.restore,
                !(hovered || isOpen) && styles.restoreHidden,
              ]}
            >
              ⤺ Restore
            </html.span>
          )}
          {!isLive && (
            <html.span
              aria-hidden={true}
              style={[styles.chevron, isOpen && styles.chevronOpen]}
            >
              ▶
            </html.span>
          )}
        </html.button>

        {/* inline confirm */}
        {!isLive && (
          <html.div style={[styles.confirm, isOpen && styles.confirmOpen]}>
            <html.div style={styles.confirmClip}>
              <html.div style={styles.confirmBody}>
                <html.p style={styles.confirmText}>
                  Make v{versionNo} live again? This publishes a new version —
                  your current site is kept in history.
                </html.p>
                <html.div style={styles.confirmActions}>
                  <Button
                    variant="primary"
                    fullWidth={isMobile}
                    onClick={onRestore}
                    disabled={busy}
                  >
                    {isRestoring ? (
                      <>
                        <html.span aria-hidden={true}>
                          <Spinner size="sm" label="Restoring" />
                        </html.span>{" "}
                        Restoring…
                      </>
                    ) : (
                      "Make live"
                    )}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={onPreview}
                    disabled={isRestoring}
                  >
                    👁 Preview
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={onToggle}
                    disabled={isRestoring}
                  >
                    Cancel
                  </Button>
                </html.div>
              </html.div>
            </html.div>
          </html.div>
        )}
      </html.div>
    </html.li>
  );
}
